import { useEffect, useRef, useState } from "react";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
} from "@mui/material";
import { Formik } from "formik";
import { AddClientSchema } from "validations";
import VuiAlert from "components/VuiAlert";
import { MAX_HOTELS_PER_CLIENT } from "variables";
import { removeEmptyKeys } from "utils/objectHelpers";
import VuiButton from "components/VuiButton";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import useCommon from "hooks/useCommon";
import { getUserInfo } from "store/slice/userSlice";
import VuiSwitch from "components/VuiSwitch";
import { getClientById } from "store/slice/clientSlice";
import VuiPhoneInput from "components/VuiPhoneInput";
import { FaTrash, FaUpload } from "react-icons/fa";
import { deleteHotel } from "store/slice/hotelsSlice";
import { resetDeleteHotelState } from "store/slice/hotelsSlice";
import { uploadBrandLogoFromFile } from "services/uploadLogo";
import { prepareBrandLogoFromFile } from "services/uploadLogo";
import OverlayLoader from "components/OverlayLoader";
import TimezoneSelectField from "components/TimeZoneSelector";

const EditClientDialog = ({ clientId, open, onClose, onSave }) => {
  const { STORE, DISPATCH } = useCommon();
  const { updateLoading, loading, error } = STORE((state) => state.clients);
  const { deleteLoading, deleteSuccess, deleteError } = STORE((state) => state.hotels);
  const formikHelpersRef = useRef({ setFieldValue: null, values: null });

  const [formData, setFormData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hotelToDelete, setHotelToDelete] = useState(null);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [uploadErrors, setUploadErrors] = useState({});

  const fetchUserInfo = async () => {
    try {
      const response = await DISPATCH(getClientById(clientId)).unwrap();
      const user = response?.data?.user || {};
      setFormData({
        name: user.name || "",
        email: user.email || "",
        contactNumber: user.contactNumber || "",
        status: user.status ?? 1,
        hotels:
          user.hotelId?.length > 0
            ? user.hotelId.map((h) => ({
                _id: h._id,
                name: h.name || "",
                address: h.address || "",
                brandLogo: h.brandLogo || "", // string URL for existing
                timezone: h.timezone || "",
              }))
            : [{ name: "", address: "", brandLogo: "" }],
      });
    } catch (err) {
      console.error("Failed to fetch user info", err);
    }
  };

  const handleCloseDialog = () => {
    setUploadErrors({});
    setUploadingIndex(null);
    setConfirmOpen(false);
    setHotelToDelete(null);
    onClose();
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleHotelChange = (index, field, value) => {
    setFormData((prev) => {
      const hotels = [...prev.hotels];
      hotels[index][field] = value;
      return { ...prev, hotels };
    });
  };

  const addHotel = () => {
    setFormData((prev) => ({
      ...prev,
      hotels: [...prev.hotels, { name: "", address: "", brandLogo: "" }],
    }));
  };

  const handleCloseDeleteDialog = () => {
    setConfirmOpen(false);
    setHotelToDelete(null);
    DISPATCH(resetDeleteHotelState());
  };

  const handleRemoveHotel = (hotel, index, values, setFieldValue) => {
    if (values.hotels.length <= 1) return;

    // Existing hotel → show confirmation dialog
    if (hotel._id) {
      setHotelToDelete(hotel);
      setConfirmOpen(true);
    }
    // New hotel → remove locally
    else {
      const updated = values.hotels.filter((_, i) => i !== index);
      setFieldValue("hotels", updated);
    }
  };

  // const handleRemoveHotel = (hotel, index) => {
  //   if (formData.hotels.length <= 1) return;

  //   // Existing hotel → show confirmation dialog
  //   if (hotel._id) {
  //     setHotelToDelete(hotel);
  //     setConfirmOpen(true);
  //   }
  //   // New hotel → remove locally
  //   else {
  //     setFormData((prev) => {
  //       const updated = [...prev.hotels];
  //       updated.splice(index, 1);
  //       return { ...prev, hotels: updated };
  //     });
  //   }
  // };

  // const handleConfirmDeleteHotel = async () => {
  //   if (!hotelToDelete?._id) return;

  //   try {
  //     await DISPATCH(deleteHotel(hotelToDelete._id)).unwrap();

  //     setFormData((prev) => ({
  //       ...prev,
  //       hotels: prev.hotels.filter((h) => h._id !== hotelToDelete._id),
  //     }));

  //     handleCloseDeleteDialog();
  //   } catch (err) {
  //     console.error("Failed to delete hotel", err);
  //   }
  // };

  const handleConfirmDeleteHotel = async () => {
    if (!hotelToDelete?._id) return;

    try {
      await DISPATCH(deleteHotel(hotelToDelete._id)).unwrap();

      const { setFieldValue, values } = formikHelpersRef.current;
      if (setFieldValue && values) {
        setFieldValue(
          "hotels",
          values.hotels.filter((h) => h._id !== hotelToDelete._id)
        );
      }

      handleCloseDeleteDialog();
    } catch (err) {
      console.error("Failed to delete hotel", err);
    }
  };

  const handleSaveChanges = async () => {
    if (!formData) return;
    try {
      if (!formData.clientName || !formData.email) {
        alert("Client name and email are required");
        return;
      }

      const payload = {
        id: clientId,
        name: formData.clientName,
        email: formData.email,
        contactNumber: formData.contactNumber,
        status: formData.status,
        hotels: formData.hotels.map((h) => ({
          _id: h._id,
          name: h.name,
          address: h.address,
          brandLogo: h.brandLogo,
          timezone: h.timezone,
        })),
      };

      onSave?.(payload);
    } catch (err) {
      console.error("Failed to save client changes", err);
      alert("Failed to save changes");
    }
  };

  const handleBrandLogoUpload = async (file, index, hotelId, setFieldValue) => {
    if (!file) return;

    setUploadErrors((prev) => ({ ...prev, [index]: null }));
    setUploadingIndex(index);

    try {
      // EXISTING HOTEL → upload immediately
      if (hotelId) {
        const res = await uploadBrandLogoFromFile(file, hotelId);
        const logoUrl = res.data.data.url;

        setFieldValue(`hotels.${index}.brandLogo`, logoUrl);
      }
      // NEW HOTEL → prepare payload only
      else {
        const preparedLogo = await prepareBrandLogoFromFile(file);
        setFieldValue(`hotels.${index}.brandLogo`, preparedLogo);
      }
    } catch (err) {
      console.error("Upload failed:", err);
      setUploadErrors((prev) => ({
        ...prev,
        [index]: err?.response?.data?.message || err?.message || "Failed to upload logo",
      }));
    } finally {
      setUploadingIndex(null);
    }
  };

  useEffect(() => {
    if (!open || !clientId) return;
    fetchUserInfo();
  }, [open, clientId, DISPATCH]);

  // Do not render dialog body until open
  if (!open) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          handleCloseDialog();
        }}
        maxWidth="md"
        fullWidth
        keepMounted
      >
        <DialogTitle sx={{ pb: 1 }}>
          <VuiTypography variant="button" fontWeight="bold">
            Edit Client
          </VuiTypography>
        </DialogTitle>

        {loading && (
          <VuiBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            maxHeight="70vh"
            minHeight="70vh"
          >
            <CircularProgress color="primary" />
          </VuiBox>
        )}

        {!loading && error && (
          <VuiTypography color="error">Failed to load client details.</VuiTypography>
        )}

        {!loading && !error && formData && (
          <Formik
            initialValues={formData}
            validationSchema={AddClientSchema}
            enableReinitialize
            onSubmit={(values) => {
              const cleanedHotels = values.hotels.map((hotel) => removeEmptyKeys(hotel));
              const cleanedPayload = removeEmptyKeys({
                ...values,
                hotels: cleanedHotels,
              });

              const payload = {
                id: clientId,
                name: cleanedPayload.name,
                email: cleanedPayload.email,
                contactNumber: cleanedPayload.contactNumber,
                status: cleanedPayload.status,
                hotels: cleanedPayload.hotels.map((h) => ({
                  _id: h._id,
                  name: h.name,
                  address: h.address,
                  brandLogo: h.brandLogo,
                  timezone: h.timezone,
                  identifierMode: "phone",
                  countryCode: h?.countryCode || "",
                  defaultPhoneNumber: h?.defaultPhoneNumber || "",
                  defaultEmail: h?.defaultEmail || "",
                })),
              };

              onSave?.(payload);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              setFieldTouched,
              setFieldValue,
            }) => {
              formikHelpersRef.current = { setFieldValue, values };

              const handleAddHotel = () => {
                if (values.hotels.length >= MAX_HOTELS_PER_CLIENT) return;
                setFieldValue("hotels", [
                  ...values.hotels,
                  { name: "", address: "", brandLogo: "", timezone: "" },
                ]);
              };

              return (
                <form onSubmit={handleSubmit}>
                  <DialogContent
                    sx={{
                      maxHeight: "70vh",
                      minHeight: "70vh",
                    }}
                  >
                    {/* Client fields */}
                    <VuiBox
                      display="grid"
                      gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                      gap="16px"
                      mb={2}
                    >
                      <VuiBox>
                        <VuiTypography variant="caption">Client Name *</VuiTypography>
                        <VuiInput
                          textOnly
                          gradient
                          fullWidth
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          error={touched.name && Boolean(errors.name)}
                          inputProps={{ maxLength: 30 }}
                        />
                        {touched.name && errors.name && (
                          <VuiTypography variant="caption" color="error">
                            {errors.name}
                          </VuiTypography>
                        )}
                      </VuiBox>

                      <VuiBox>
                        <VuiTypography variant="caption">Email *</VuiTypography>
                        <VuiInput
                          gradient
                          fullWidth
                          name="email"
                          value={values.email}
                          onChange={handleChange}
                          error={touched.email && Boolean(errors.email)}
                          inputProps={{ maxLength: 80 }}
                        />
                        {touched.email && errors.email && (
                          <VuiTypography variant="caption" color="error">
                            {errors.email}
                          </VuiTypography>
                        )}
                      </VuiBox>
                    </VuiBox>

                    <VuiBox mb={2}>
                      <VuiPhoneInput
                        label="Contact Number *"
                        value={values.contactNumber}
                        defaultCountry="US"
                        onChange={(value) => {
                          setFieldValue("contactNumber", value || "");
                        }}
                        onBlur={() => setFieldTouched("contactNumber", true)}
                        // onChange={(value) =>
                        //   handleChange({
                        //     target: { name: "contactNumber", value },
                        //   })
                        // }
                        error={touched.contactNumber && errors.contactNumber}
                      />
                    </VuiBox>

                    <VuiTypography variant="button" fontWeight="medium" mb={1}>
                      Hotels *
                    </VuiTypography>

                    {typeof errors.hotels === "string" && (
                      <VuiTypography variant="caption" color="error">
                        {errors.hotels}
                      </VuiTypography>
                    )}

                    {values.hotels.map((hotel, index) => {
                      const existingHotelsCount = values.hotels.filter((h) => h._id).length;
                      const isOnlyExistingHotel = hotel._id && existingHotelsCount === 1;

                      return (
                        <VuiBox
                          key={hotel._id || index}
                          mb={2}
                          sx={{
                            border: "1px solid rgba(255,255,255,0.15)",
                            borderRadius: "12px",
                            p: 2,
                          }}
                        >
                          <VuiBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                          >
                            <VuiTypography variant="button" />

                            {!isOnlyExistingHotel && (
                              <VuiButton
                                variant="text"
                                color="error"
                                size="small"
                                onClick={() =>
                                  handleRemoveHotel(hotel, index, values, setFieldValue)
                                }
                                sx={{ minWidth: "auto", px: 1 }}
                              >
                                <FaTrash size={12} style={{ marginRight: 8 }} />
                                Delete
                              </VuiButton>
                            )}
                          </VuiBox>

                          <VuiBox mb={1}>
                            <VuiTypography variant="caption">Hotel Name *</VuiTypography>
                            <VuiInput
                              gradient
                              fullWidth
                              value={hotel.name}
                              onChange={(e) =>
                                setFieldValue(`hotels.${index}.name`, e.target.value)
                              }
                            />
                            {touched.hotels?.[index]?.name && errors.hotels?.[index]?.name && (
                              <VuiTypography variant="caption" color="error">
                                {errors.hotels[index].name}
                              </VuiTypography>
                            )}
                          </VuiBox>

                          <VuiBox mb={1}>
                            <VuiTypography variant="caption">Address *</VuiTypography>
                            <VuiInput
                              gradient
                              fullWidth
                              value={hotel.address}
                              onChange={(e) =>
                                setFieldValue(`hotels.${index}.address`, e.target.value)
                              }
                            />
                            {touched.hotels?.[index]?.address &&
                              errors.hotels?.[index]?.address && (
                                <VuiTypography variant="caption" color="error">
                                  {errors.hotels[index].address}
                                </VuiTypography>
                              )}
                          </VuiBox>

                          <VuiBox mb={1}>
                            <TimezoneSelectField
                              value={hotel.timezone}
                              onChange={(value) => setFieldValue(`hotels.${index}.timezone`, value)}
                              touched={touched.hotels?.[index]?.timezone}
                              error={errors.hotels?.[index]?.timezone}
                            />
                          </VuiBox>

                          <VuiBox>
                            <VuiTypography variant="caption" mb={1}>
                              Brand Logo
                            </VuiTypography>

                            {!!hotel.brandLogo && (
                              <VuiBox mb={1} display="flex" justifyContent="center">
                                <img
                                  src={
                                    typeof hotel.brandLogo === "string"
                                      ? hotel.brandLogo
                                      : hotel.brandLogo.dataBase64
                                  }
                                  alt="Brand Logo"
                                  style={{
                                    height: 88,
                                    borderRadius: 6,
                                    border: "1px solid rgba(255,255,255,0.2)",
                                  }}
                                />
                              </VuiBox>
                            )}

                            <VuiBox display="flex" justifyContent="center">
                              <VuiButton
                                component="label"
                                color={hotel?.brandLogo ? "success" : "primary"}
                                variant="outlined"
                                disabled={uploadingIndex === index}
                                sx={{
                                  "&.Mui-disabled": {
                                    color: "white !important",
                                    borderColor: "rgba(255,255,255,0.3)",
                                    opacity: 0.7,
                                  },
                                }}
                              >
                                <FaUpload size={12} style={{ marginRight: 8 }} />
                                {uploadingIndex === index
                                  ? "Uploading..."
                                  : hotel?.brandLogo
                                    ? "Update Logo"
                                    : "Upload Logo"}

                                <input
                                  type="file"
                                  hidden
                                  accept="image/png,image/jpeg"
                                  onChange={(e) =>
                                    handleBrandLogoUpload(
                                      e.target.files[0],
                                      index,
                                      hotel._id,
                                      setFieldValue
                                    )
                                  }
                                />
                              </VuiButton>
                            </VuiBox>

                            {uploadErrors[index] && (
                              <VuiBox mt={1} display="flex" justifyContent="center">
                                <VuiTypography variant="caption" color="error" textAlign="center">
                                  {uploadErrors[index]}
                                </VuiTypography>
                              </VuiBox>
                            )}
                          </VuiBox>
                        </VuiBox>
                      );
                    })}

                    <VuiButton variant="outlined" color="primary" onClick={handleAddHotel}>
                      + Add Hotel
                    </VuiButton>
                  </DialogContent>

                  <DialogActions sx={{ px: 3, pb: 2 }}>
                    <VuiButton
                      variant="outlined"
                      color="primary"
                      onClick={handleCloseDialog}
                      disabled={uploadingIndex !== null}
                    >
                      Cancel
                    </VuiButton>

                    <VuiButton
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={updateLoading || uploadingIndex !== null}
                    >
                      {updateLoading ? "Saving..." : "Save Changes"}
                    </VuiButton>
                  </DialogActions>
                </form>
              );
            }}
          </Formik>
        )}
        <OverlayLoader open={updateLoading} message="Updating..." />
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          setConfirmOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          {deleteError && (
            <VuiBox
              mb={2}
              display="flex"
              justifyContent="center"
              alignItems="center"
              textAlign="center"
            >
              <VuiTypography variant="caption" color="error">
                <strong>Error:</strong>
                {deleteError}
              </VuiTypography>
            </VuiBox>
          )}

          <VuiTypography variant="body2">
            Are you sure you want to delete{" "}
            <strong>
              {""} {hotelToDelete?.name || "this hotel"}
            </strong>
            ?
          </VuiTypography>
          <VuiTypography variant="caption">
            This hotel will be deleted and associated users may lose access.
          </VuiTypography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <VuiButton variant="outlined" color="primary" onClick={handleCloseDeleteDialog}>
            Cancel
          </VuiButton>

          <VuiButton
            variant="gradient"
            color="error"
            onClick={handleConfirmDeleteHotel}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete Hotel"}
          </VuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditClientDialog;
